import { useCallback, useEffect, useMemo } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  LabsFullscreenProvider,
  type LabsFullscreenContextValue,
} from '@reflex/ui/components/shared/LabsFullscreen';
import { useAuth } from '@/hooks/auth/useAuth';
import { usePlugins } from '@/hooks/config/usePlugins';
import { useActiveOrgSlug } from '@/lib/useOrgRoutes';
import { ORG_PATH_PREFIX, ROUTES } from '@/lib/routes';
import { useNavSources } from '../NavBar/useNavSources';
import { LabsShellView } from './LabsShellView';
import { useTakeoverEscape } from './useTakeoverEscape';
import { isLabsCorePath } from './takeover-paths';
import { useLabsFullscreenState } from './useLabsFullscreenState';

/**
 * Strip the `/orgs/:slug` prefix (or the legacy slug-less leading segment)
 * so the result matches the root-relative route paths plugin manifests
 * declare (e.g. `/town`). Legacy `/acme/town` shapes hop through
 * `LegacyOrgPathRedirect` to the org-scoped URL, so the post-redirect
 * pathname is always `/orgs/...` here.
 */
function stripOrgPrefix(pathname: string): string {
  const segments = pathname.split('/').filter(Boolean);
  if (segments[0] === ORG_PATH_PREFIX) return '/' + segments.slice(2).join('/');
  return '/' + segments.join('/');
}

/**
 * True when the current path lands on a route that declared `fullscreen:
 * true` in its manifest. Game routes are static (no params), so an exact
 * match — or a prefix match for nested game paths — is enough.
 */
function useFullscreenAvailable(
  resolvedRoutes: ReadonlyArray<{ path: string; fullscreen?: boolean }>,
  pathname: string,
): boolean {
  return useMemo(() => {
    const tail = stripOrgPrefix(pathname);
    for (const r of resolvedRoutes) {
      if (!r.fullscreen) continue;
      if (tail === r.path || tail.startsWith(r.path + '/')) return true;
    }
    return false;
  }, [resolvedRoutes, pathname]);
}

/**
 * Layout route for the Labs takeover: Chat, the Misc contents, and the
 * Plugins overview render inside this shell, and the app shell drops its
 * sidebar on these paths (see `isLabsPath`). Back and `esc` return to the
 * org's agent sessions page — but `esc` only on the core labs pages;
 * immersive plugin pages (games, demos) keep `esc` to themselves and
 * exit through the chip.
 *
 * Fullscreen is a per-page capability: only routes that set
 * `fullscreen: true` in their manifest (`definePage({ fullscreen: true })`)
 * get it — the games. The shell reads that flag off the resolved routes
 * and only then hides the aside + provides the fullscreen context the
 * game's own header toggle consumes. `?fullscreen=true` is a no-op (and
 * is cleared) on any other Labs page, so the feature stays scoped to
 * games even under a stale deep-link.
 */
export function LabsShell() {
  const slug = useActiveOrgSlug();
  const { activeMembership } = useAuth();
  const { labsDrillGroups, labsChatItem } = useNavSources();
  const { routes } = usePlugins();
  const navigate = useNavigate();
  const location = useLocation();
  const { fullscreen, setFullscreen, toggleFullscreen } = useLabsFullscreenState();
  const available = useFullscreenAvailable(routes ?? [], location.pathname);

  const goBack = useCallback(() => {
    const fallback = slug ? ROUTES.org(slug).runs : ROUTES.root;
    void navigate(fallback);
  }, [navigate, slug]);

  // Clear a stale `?fullscreen=true` if the current route isn't a game.
  // Without this, a deep-link to `/orgs/acme/chat?fullscreen=true` (or the
  // browser Back button landing there) would hide the aside on a page
  // that has no toggle to restore it. `available` is derived from the
  // resolved routes, so it stays accurate across in-shell navigation.
  useEffect(() => {
    if (!available && fullscreen) setFullscreen(false);
  }, [available, fullscreen, setFullscreen]);

  // The fullscreen state actually applied to this view: only on when the
  // current route is a game. Game pages read the same context for their
  // header toggle.
  const viewFullscreen = available && fullscreen;

  // Leave the takeover on `esc` from the core labs pages — but never while
  // fullscreen owns the key (fullscreen `esc` exits fullscreen instead).
  useTakeoverEscape(goBack, isLabsCorePath(location.pathname) && !viewFullscreen);
  // In fullscreen, `esc` drops back to the chrome view. Same closest-layer
  // guard from `useTakeoverEscape` keeps a focused input / open dialog /
  // xyflow canvas from triggering it, so a game using `esc` for its own
  // menus still wins.
  useTakeoverEscape(() => setFullscreen(false), viewFullscreen);

  const fullscreenContext = useMemo<LabsFullscreenContextValue>(
    () => ({ available, fullscreen: viewFullscreen, toggle: toggleFullscreen }),
    [available, viewFullscreen, toggleFullscreen],
  );

  return (
    <LabsFullscreenProvider value={fullscreenContext}>
      <LabsShellView
        orgName={activeMembership?.organization.name ?? 'Organization'}
        activePath={location.pathname}
        chatItem={labsChatItem}
        groups={labsDrillGroups}
        onBack={goBack}
        fullscreen={viewFullscreen}
      >
        <Outlet />
      </LabsShellView>
    </LabsFullscreenProvider>
  );
}
